import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Award, Users, Quote, ArrowRight, ChevronRight, Medal, Calendar, Building } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { teamPerformance } from '../data/mock';
import { getOptimizedImageUrl } from '../lib/utils';

const PerformancePage = () => {
  return (
    <div className="min-h-screen bg-cream" data-testid="performance-page">
      <Header />

      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-16 md:py-24 overflow-hidden bg-gradient-to-br from-cream via-pragmatic/10 to-richolive/5">
          <div className="absolute inset-0">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-pragmatic/15 rounded-full blur-3xl" />
            <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-richolive/10 rounded-full blur-3xl" />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto">
              <span className="inline-block text-dwarfpony text-sm font-semibold tracking-wider uppercase mb-4">
                Our Journey
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-deepwalnut mb-6">
                Team <span className="text-richolive">Performance</span>
              </h1>
              <p className="text-xl text-heavyrain leading-relaxed">
                From 2017 to 2024 — A legacy of excellence, innovation, and breaking barriers
              </p>
            </div>
          </div>
        </section>

        {/* Timeline Section */}
        <section className="py-16 bg-cream-dark">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Year Navigation */}
            <div className="flex flex-wrap justify-center gap-3 mb-16">
              {teamPerformance.map((team) => (
                <Link
                  key={team.year}
                  to={`/performance/${team.year}`}
                  className="px-4 py-2 bg-cream border border-pragmatic/30 rounded-full text-deepwalnut font-semibold hover:border-dwarfpony hover:bg-gradient-to-r hover:from-pragmatic/20 hover:to-richolive/10 transition-all"
                >
                  {team.year}
                </Link>
              ))}
            </div>

            {/* Timeline */}
            <div className="relative">
              {/* Vertical Line */}
              <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-dwarfpony via-richolive to-dwarfpony md:-translate-x-1/2" />

              <div className="space-y-16">
                {teamPerformance.map((team, index) => (
                  <div
                    key={team.year}
                    id={`year-${team.year}`}
                    className={`relative flex flex-col md:flex-row gap-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                  >
                    {/* Year Badge - Mobile */}
                    <Link
                      to={`/performance/${team.year}`}
                      className="md:hidden absolute left-0 w-16 h-16 rounded-full bg-gradient-to-br from-dwarfpony to-dwarfpony-dark flex items-center justify-center text-white font-bold text-lg shadow-xl shadow-dwarfpony/30 z-10 hover:scale-110 transition-transform"
                    >
                      {team.year}
                    </Link>

                    {/* Content Card */}
                    <div className={`flex-1 ml-20 md:ml-0 ${index % 2 === 0 ? 'md:pr-12' : 'md:pl-12'}`}>
                      <Link
                        to={`/performance/${team.year}`}
                        className="block bg-cream border border-pragmatic/30 rounded-2xl overflow-hidden hover:border-dwarfpony hover:shadow-xl transition-all group"
                      >
                        {/* Image */}
                        <div className="relative aspect-video overflow-hidden">
                          <img
                            src={getOptimizedImageUrl(team.image, 800)}
                            srcSet={`
                              ${getOptimizedImageUrl(team.image, 400)} 400w,
                              ${getOptimizedImageUrl(team.image, 800)} 800w
                            `}
                            sizes="(max-width: 768px) 100vw, 50vw"
                            alt={team.team}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            style={{ objectPosition: team.imagePosition || 'center center' }}
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-midnight via-midnight/50 to-transparent" />

                          {/* Location Badge */}
                          <div className="absolute top-4 left-4 bg-cream/90 backdrop-blur-md rounded-full px-4 py-2 flex items-center gap-2 shadow-md">
                            <MapPin size={16} className="text-dwarfpony" />
                            <span className="text-deepwalnut text-sm font-medium">{team.location}</span>
                          </div>

                          {/* Rank Badge */}
                          <div className="absolute top-4 right-4 bg-gradient-to-r from-dwarfpony to-dwarfpony-dark rounded-full px-4 py-2 shadow-lg">
                            <span className="text-white font-bold">{team.rank}</span>
                          </div>
                        </div>

                        {/* Details */}
                        <div className="p-6">
                          <h3 className="text-2xl font-bold text-deepwalnut mb-2 group-hover:text-dwarfpony transition-colors">{team.team}</h3>

                          <div className="flex flex-wrap gap-4 mb-4">
                            <div className="flex items-center gap-2 text-heavyrain text-sm">
                              <Building size={16} className="text-richolive" />
                              {team.school}
                            </div>
                            <div className="flex items-center gap-2 text-heavyrain text-sm">
                              <Calendar size={16} className="text-richolive" />
                              {team.year}
                            </div>
                          </div>

                          {/* Problem Statement */}
                          <div className="bg-gradient-to-r from-pragmatic/20 to-richolive/10 rounded-xl p-4 mb-4 border border-pragmatic/20">
                            <p className="text-heavyrain text-xs uppercase tracking-wider mb-1">Problem Statement</p>
                            <p className="text-richolive font-semibold">{team.problemStatement}</p>
                          </div>

                          {/* Medals */}
                          {team.medals && team.medals.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-4">
                              {team.medals.map((medal, i) => (
                                <span
                                  key={i}
                                  className="inline-flex items-center gap-1 bg-pragmatic/30 text-dwarfpony-dark px-3 py-1 rounded-full text-sm"
                                >
                                  <Medal size={14} />
                                  {medal}
                                </span>
                              ))}
                            </div>
                          )}

                          {/* Mentors, Sponsors & Supporters */}
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
                            {team.mentors && team.mentors.length > 0 && (
                              <div>
                                <p className="text-heavyrain text-xs uppercase tracking-wider mb-2">Mentors</p>
                                <div className="flex flex-wrap gap-1">
                                  {team.mentors.map((mentor, i) => (
                                    <span key={i} className="text-deepwalnut text-sm bg-cream-dark px-2 py-1 rounded">
                                      {mentor}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {team.supporters && team.supporters.length > 0 && (
                              <div>
                                <p className="text-heavyrain text-xs uppercase tracking-wider mb-2">Supporters</p>
                                <div className="flex flex-wrap gap-1">
                                  {team.supporters.map((supporter, i) => (
                                    <span key={i} className="text-deepwalnut text-sm bg-cream-dark px-2 py-1 rounded">
                                      {supporter}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {team.sponsors && team.sponsors.length > 0 && (
                              <div>
                                <p className="text-heavyrain text-xs uppercase tracking-wider mb-2">Sponsors</p>
                                <div className="flex flex-wrap gap-1">
                                  {team.sponsors.map((sponsor, i) => (
                                    <span key={i} className="text-deepwalnut text-sm bg-cream-dark px-2 py-1 rounded">
                                      {sponsor}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Team Members */}
                          {team.students && team.students.length > 0 && (
                            <div className="mb-4">
                              <p className="text-heavyrain text-xs uppercase tracking-wider mb-2">Team Members</p>
                              <div className="flex flex-wrap gap-1">
                                {team.students.slice(0, 5).map((student, i) => (
                                  <span key={i} className="text-deepwalnut text-xs bg-pragmatic/20 px-2 py-1 rounded-full">
                                    {student}
                                  </span>
                                ))}
                                {team.students.length > 5 && (
                                  <span className="text-heavyrain text-xs px-2 py-1">+{team.students.length - 5} more</span>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Quote */}
                          <div className="relative bg-gradient-to-r from-pragmatic/20 to-richolive/10 border border-pragmatic/30 rounded-xl p-4">
                            <Quote className="absolute -top-2 -left-2 w-6 h-6 text-pragmatic" />
                            <p className="text-deepwalnut text-sm italic pl-4">"{team.quote}"</p>
                          </div>

                          {/* View Details Link */}
                          <div className="flex items-center justify-end gap-1 mt-4 text-richolive text-sm font-medium group-hover:text-dwarfpony transition-colors">
                            View Full Details
                            <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
                          </div>
                        </div>
                      </Link>
                    </div>

                    {/* Year Badge - Desktop */}
                    <Link
                      to={`/performance/${team.year}`}
                      className="hidden md:flex absolute left-1/2 -translate-x-1/2 w-20 h-20 rounded-full bg-gradient-to-br from-dwarfpony to-richolive items-center justify-center text-white font-bold text-xl shadow-xl z-10 hover:scale-110 transition-transform"
                    >
                      {team.year}
                    </Link>

                    {/* Empty space for alternating layout */}
                    <div className="hidden md:block flex-1" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gradient-to-br from-midnight to-richolive-dark">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Be Part of the <span className="text-pragmatic">Next Chapter</span>
            </h2>
            <p className="text-heavyrain text-lg mb-8">
              Join our team and represent India at FIRST Global 2027
            </p>
            <Link to="/contact">
              <Button className="bg-gradient-to-r from-dwarfpony to-dwarfpony-dark hover:from-dwarfpony-dark hover:to-dwarfpony text-white font-semibold px-8 py-6 text-lg shadow-xl shadow-dwarfpony/25 group">
                Apply Now
                <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PerformancePage;
